// src/components/blog/TableOfContents.tsx
//
// Sticky table of contents generated by scanning the rendered article for
// h2 and h3 elements. Each heading is given an id via react-markdown's
// slugger so links scroll smoothly.

'use client'

import { useEffect, useState } from 'react'

export interface TocHeading {
  id: string
  text: string
  level: 2 | 3
}

export interface TableOfContentsProps {
  contentSelector?: string
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
}

export default function TableOfContents({
  contentSelector = '.blog-article-content',
}: TableOfContentsProps) {
  const [headings, setHeadings] = useState<TocHeading[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)

  useEffect(() => {
    const root = document.querySelector(contentSelector)
    if (!root) return

    const hElements = Array.from(root.querySelectorAll('h2, h3'))
    const list: TocHeading[] = hElements.map((el) => {
      let id = el.id
      if (!id) {
        id = slugify(el.textContent ?? '')
        el.id = id
      }
      return {
        id,
        text: el.textContent ?? '',
        level: el.tagName === 'H3' ? 3 : 2,
      }
    })
    setHeadings(list)

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) setActiveId(visible[0].target.id)
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: [0, 1] },
    )
    hElements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [contentSelector])

  if (headings.length === 0) return null

  return (
    <nav
      aria-label="Table of contents"
      className="sticky top-24 hidden max-h-[calc(100vh-7rem)] overflow-y-auto text-sm lg:block"
    >
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        On this page
      </p>
      <ul className="space-y-2 border-l border-border">
        {headings.map((h) => {
          const isActive = h.id === activeId
          return (
            <li key={h.id}>
              <a
                href={`#${h.id}`}
                className={
                  'block border-l-2 pl-3 transition ' +
                  (h.level === 3 ? 'ml-4 ' : '') +
                  (isActive
                    ? 'border-brand text-brand-ink'
                    : 'border-transparent text-muted-foreground hover:text-foreground')
                }
              >
                {h.text}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
